import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Footer from '../../components/common/Footer';
import Navbar from '../../components/common/Navbar';
import { useAuth } from '../../hooks/useAuth';
import api from '../../services/api';

const LandingPage = () => {
    const navigate = useNavigate();
    const { user, isAuthenticated } = useAuth();
    const [stats, setStats] = useState([
        { label: 'Exam Papers', value: '...' },
        { label: 'Students', value: '...' },
        { label: 'Departments', value: '...' },
        { label: 'Access', value: 'Free' }
    ]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        // Redirect logged-in users to their dashboard
        if (isAuthenticated()) {
            if (user?.role === 'admin') {
                navigate('/admin/dashboard', { replace: true });
            } else {
                navigate('/dashboard', { replace: true });
            }
        } else {
            fetchPublicStats();
        }
    }, [user, isAuthenticated, navigate]);

    const fetchPublicStats = async () => {
        try {
            const response = await api.get('/auth/public-stats');
            if (response.data.success) {
                const data = response.data.data;
                setStats([
                    { label: 'Exam Papers', value: data.total_papers.toLocaleString() },
                    { label: 'Students', value: data.total_students.toLocaleString() },
                    { label: 'Departments', value: data.total_departments },
                    { label: 'Access', value: 'Free' }
                ]);
            }
        } catch (error) {
            console.error('Error fetching stats:', error);
            // Keep default values if fetch fails
            setStats([
                { label: 'Exam Papers', value: '1000+' },
                { label: 'Students', value: '500+' },
                { label: 'Departments', value: '12' },
                { label: 'Access', value: 'Free' }
            ]);
        } finally {
            setLoading(false);
        }
    };

    const features = [
        {
            icon: 'folder_open',
            title: 'Organized',
            description: 'Papers sorted by department and semester for easy navigation.'
        },
        {
            icon: 'verified',
            title: 'Verified',
            description: 'High-quality answers vetted by toppers and subject experts.'
        },
        {
            icon: 'download',
            title: 'Instant',
            description: 'One-click PDF downloads for seamless offline study sessions.'
        },
        {
            icon: 'devices',
            title: 'Responsive',
            description: 'Study on any device with our fully mobile-optimized interface.'
        }
    ];

    return (
        <div className="min-h-screen bg-background-light dark:bg-background-dark">
            <Navbar />

            <main>
                {/* Hero Section */}
                <section className="relative">
                    <div 
                        className="w-full py-12 px-4 sm:px-10 lg:py-24"
                        style={{
                            backgroundImage: `url('https://scontent.fisb17-1.fna.fbcdn.net/v/t39.30808-6/485969929_1089165766586877_6897099126086274893_n.jpg?_nc_cat=102&ccb=1-7&_nc_sid=86c6b0&_nc_ohc=_2-dtYQHnKAQ7kNvwG7yfiD&_nc_oc=AdlLtSQ2KriY6qmhwI_OSABw3KDuJnSNoJsbohpHUU7MvlmtZDLWIMriabKutr-Fxuu2dOmPTHIcEiBXZQtrP8iT&_nc_zt=23&_nc_ht=scontent.fisb17-1.fna&_nc_gid=DcmDHg3swlIThrZ7VK2Cqg&oh=00_AfpNaLr5bSroUiDThXvQx0Uon1JoSXd0Oi0_ySDypDs-BA&oe=6972E2CF')`,
                            backgroundSize: 'cover',
                            backgroundPosition: 'center',
                            backgroundRepeat: 'no-repeat',
                            backgroundColor: '#1e3a8a', // Primary color as fallback
                            backgroundBlendMode: 'overlay',
                        }}
                    >
                        <div className="max-w-[1200px] mx-auto">
                            <div className="flex flex-col items-center text-center">
                                <div className="flex flex-col gap-4">
                                    <h1 className="text-white text-4xl font-black leading-tight tracking-tight sm:text-5xl lg:text-6xl">
                                        Access Past Papers <br className="hidden sm:block" /> Anytime, Anywhere
                                    </h1>
                                    <p className="text-white/90 text-base font-normal max-w-2xl mx-auto sm:text-lg">
                                        The ultimate repository for COMSATS University students to excel in their examinations.
                                        Find mids, finals, and quizzes in one place.
                                    </p>

                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Stats Section */}
                <section className="py-12 bg-white dark:bg-background-dark">
                    <div className="max-w-[960px] mx-auto px-4 sm:px-10">
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                            {stats.map((stat, index) => (
                                <div
                                    key={index}
                                    className="flex flex-col gap-2 rounded-xl p-8 border border-[#dfe0e2] dark:border-gray-800 bg-background-light dark:bg-gray-900/50 text-center"
                                >
                                    <p className="text-[#6e757c] dark:text-gray-400 text-sm font-semibold uppercase tracking-widest">
                                        {stat.label}
                                    </p>
                                    <p className="text-primary tracking-tight text-4xl font-black">
                                        {loading && index < 3 ? (
                                            <span className="animate-pulse">...</span>
                                        ) : (
                                            stat.value
                                        )}
                                    </p>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* Feature Section */}
                <section className="py-20 bg-background-light dark:bg-gray-950">
                    <div className="max-w-[960px] mx-auto px-4 sm:px-10">
                        <div className="flex flex-col gap-12">
                            <div className="flex flex-col gap-4 text-center">
                                <h2 className="text-[#131416] dark:text-white text-3xl font-black tracking-tight sm:text-4xl">
                                    Everything You Need to Excel
                                </h2>
                                <p className="text-[#6e757c] dark:text-gray-400 text-lg max-w-2xl mx-auto">
                                    Designed by COMSATS alumni for the next generation of top achievers.
                                </p>
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                                {features.map((feature, index) => (
                                    <div
                                        key={index}
                                        className="flex flex-col gap-4 rounded-xl border border-[#dfe0e2] dark:border-gray-800 bg-white dark:bg-background-dark p-6 transition-transform hover:-translate-y-1"
                                    >
                                        <div className="bg-primary/10 text-primary w-12 h-12 flex items-center justify-center rounded-lg">
                                            <span className="material-symbols-outlined">{feature.icon}</span>
                                        </div>
                                        <div>
                                            <h3 className="text-[#131416] dark:text-white text-lg font-bold mb-2">
                                                {feature.title}
                                            </h3>
                                            <p className="text-[#6e757c] dark:text-gray-400 text-sm leading-relaxed">
                                                {feature.description}
                                            </p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </section>

                {/* CTA Section */}
                <section className="py-20 bg-white dark:bg-background-dark overflow-hidden relative">
                    <div className="max-w-[960px] mx-auto px-4 sm:px-10 text-center">
                        <div className="relative z-10 flex flex-col items-center gap-8">
                            <div className="flex flex-col gap-4">
                                <h2 className="text-[#131416] dark:text-white text-3xl font-black tracking-tight sm:text-4xl">
                                    Ready to Ace Your Exams?
                                </h2>
                                <p className="text-[#6e757c] dark:text-gray-400 text-lg max-w-xl mx-auto">
                                    Join thousands of COMSATS students and get access to the best study resources today.
                                    No subscription required.
                                </p>
                            </div>
                            <button
                                onClick={() => navigate('/signup')}
                                className="flex min-w-[240px] cursor-pointer items-center justify-center rounded-xl h-14 px-8 bg-primary text-white text-lg font-bold shadow-lg shadow-primary/25 hover:bg-[#3d5872] transition-all"
                            >
                                Create Free Account
                            </button>
                            <p className="text-sm text-[#6e757c]">
                                Looking for faculty access?{' '}
                                <a href="#" className="text-primary font-semibold hover:underline">
                                    Click here
                                </a>
                            </p>
                        </div>
                    </div>

                    {/* Background element */}
                    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full h-full opacity-5 pointer-events-none">
                        <svg viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg">
                            <path
                                d="M44.7,-76.4C58.8,-69.2,71.8,-59.1,79.6,-45.8C87.4,-32.5,90,-16.3,88.5,-0.9C87,14.5,81.4,29,73.6,42.3C65.8,55.6,55.9,67.7,43.2,75.1C30.6,82.5,15.3,85.2,0.4,84.5C-14.5,83.8,-29,79.7,-42.2,72.6C-55.5,65.5,-67.5,55.4,-75.4,42.8C-83.3,30.3,-87.1,15.1,-87.3,-0.1C-87.5,-15.4,-84.1,-30.7,-76.3,-44C-68.5,-57.3,-56.3,-68.6,-42.3,-75.8C-28.3,-83.1,-14.1,-86.3,0.1,-86.5C14.3,-86.7,28.7,-83.7,44.7,-76.4Z"
                                fill="#1e1f4a"
                                transform="translate(100 100)"
                            />
                        </svg>
                    </div>
                </section>
            </main>

            <Footer />
        </div>
    );
};

export default LandingPage;
